import { createModuleApi } from '@wit-support/admin-client-core';
const api = createModuleApi({ errorFormat: 'server-message' });
export function setChargebacksApiBaseUrl(url) {
    api.setBaseUrl(url);
}
/** Размер страницы списка кейсов (keyset-пагинация). */
export const CASES_PAGE_SIZE = 50;
/**
 * Разбирает сумму, введённую оператором в МАЖОРНЫХ единицах («139.38»), в целые
 * минорные, которыми фильтр ходит на провод.
 *
 * Масштаб здесь жёстко двузначный и это осознанно: в поле фильтра валюта не
 * выбирается (кейсы на странице бывают в разных валютах), а угадывать масштаб
 * не по чему. Оператор фильтрует «примерно от и до», и цена ошибки — не та
 * строка в выдаче, а не неверная сумма в письме. Поэтому же поле подписано
 * «сумма от / до» без символа валюты.
 *
 * Возвращает `undefined` и на пустой строке, и на мусоре: фильтр, который не
 * распарсился, обязан НЕ сужать выборку, а не сужать её нулём.
 */
export function parseAmountToCents(raw) {
    const t = raw.trim().replace(',', '.');
    if (t === '')
        return undefined;
    if (!/^-?\d+(\.\d{0,2})?$/.test(t))
        return undefined;
    return Math.round(Number(t) * 100);
}
/** Собирает query-строку списка. Вынесено отдельно, чтобы тестировать без сети. */
export function casesQueryString(f, cursor, limit = CASES_PAGE_SIZE) {
    const p = new URLSearchParams();
    if (f.source)
        p.set('source', f.source);
    if (f.network)
        p.set('network', f.network);
    if (f.kind)
        p.set('kind', f.kind);
    if (f.identificationStatus)
        p.set('identificationStatus', f.identificationStatus);
    if (f.reactionStatus)
        p.set('reactionStatus', f.reactionStatus);
    // Только включённый переключатель попадает на провод. `actionRequired=false`
    // сервер понимает как ПРОТИВОПОЛОЖНУЮ очередь, а выключенный фильтр обязан
    // не сужать выборку вообще.
    if (f.actionRequired)
        p.set('actionRequired', 'true');
    if (f.from)
        p.set('from', f.from);
    if (f.to)
        p.set('to', f.to);
    // Проверка на `undefined`, а не на истинность: 0 — законная граница
    // («от 0»), и `if (f.amountFrom)` её бы молча выбросил.
    if (f.amountFrom !== undefined)
        p.set('amountFrom', String(f.amountFrom));
    if (f.amountTo !== undefined)
        p.set('amountTo', String(f.amountTo));
    if (f.q)
        p.set('q', f.q);
    p.set('limit', String(limit));
    if (cursor)
        p.set('cursor', cursor);
    return `?${p.toString()}`;
}
export async function listCases(filters, cursor, limit = CASES_PAGE_SIZE) {
    return api.get(`/api/chargebacks/cases${casesQueryString(filters, cursor, limit)}`);
}
/** Карточка кейса: сам кейс, его алерты, кандидаты матчера и диалоги поддержки. */
export async function getCase(id) {
    return api.get(`/api/chargebacks/cases/${encodeURIComponent(id)}`);
}
/** Ручная привязка клиента (`chargebacks.identify`). Решение липкое — матчер его не перетрёт. */
export async function attachCaseIdentity(id, witUserId) {
    return api.post(`/api/chargebacks/cases/${encodeURIComponent(id)}/identity`, {
        witUserId,
    });
}
/**
 * Сменить наш статус разбора кейса (`chargebacks.case_status`, member-plus).
 *
 * Смысл значений — в подсказке селекта на карточке и в
 * `RiskAlertStore.setCaseStatus`. `actioned` доступен и вручную: решение по
 * кейсу бывает отправлено мимо нас (прямо в консоли источника или эквайером),
 * и без него оператор выберет «Закрыт», потеряв различие.
 */
export async function setCaseStatus(id, status) {
    return api.post(`/api/chargebacks/cases/${encodeURIComponent(id)}/status`, { status });
}
export async function detachCaseIdentity(id) {
    const r = await api.del(`/api/chargebacks/cases/${encodeURIComponent(id)}/identity`);
    if (!r.ok)
        throw new Error(await api.extractServerError(r));
}
/** Переспросить матчер платёжного сервиса. */
export async function rematchCase(id) {
    return api.post(`/api/chargebacks/cases/${encodeURIComponent(id)}/rematch`, {});
}
/**
 * Отправить reason-код в источник. Операция НЕОБРАТИМА: у провайдера
 * учитывается только первый апдейт статуса, поэтому повторная отправка не
 * ретраится, а запрещается — и на сервере (частичный уникальный индекс
 * `risk_alert_action_one_sent`), и здесь в UI.
 */
export async function sendAlertAction(alertId, body) {
    return api.post(`/api/chargebacks/alerts/${encodeURIComponent(alertId)}/action`, body);
}
// ───────────────────────────── Аккаунты источников ─────────────────────────
/**
 * Список аккаунтов источников. Ключ НИКОГДА не приезжает на клиент — только
 * флаг `hasApiKey`: секрет лежит в `risk_provider_account.config`
 * зашифрованным, и роут отдаёт факт его наличия, а не значение.
 *
 * Конверт `{accounts}` разворачивается ЗДЕСЬ, а не в компоненте: форма ответа
 * — деталь провода, и экрану от неё нужен только массив.
 */
export async function listAccounts() {
    const r = await api.get('/api/chargebacks/accounts');
    return r.accounts;
}
export async function createAccount(body) {
    return api.post('/api/chargebacks/accounts', body);
}
/**
 * Правка аккаунта. `apiKey` ОТСУТСТВУЕТ в теле, когда оператор не трогал поле
 * секрета, и это не мелочь формы: затереть рабочий ключ пустотой значит
 * остановить загрузку алертов, причём незаметно. `pruneUntouchedSecrets` формы
 * выбрасывает нетронутое секретное поле целиком — ровно поэтому поле
 * опционально и в контракте.
 */
export async function updateAccount(id, body) {
    return api.patch(`/api/chargebacks/accounts/${encodeURIComponent(id)}`, body);
}
/**
 * Ошибка запроса с СОХРАНЁННЫМ кодом ответа.
 *
 * Нужна ровно там, где по коду принимается РАЗНОЕ решение, а не только
 * показывается разный текст: 409 при удалении аккаунта означает «у аккаунта
 * есть загруженные алерты», и правильное действие — не повторить удаление, а
 * перевести аккаунт в «отключён». Разбирать это по подстроке в сообщении
 * сервера нельзя: текст меняется и переводится, код — нет.
 */
export class ChargebacksRequestError extends Error {
    status;
    constructor(message, status) {
        super(message);
        this.name = 'ChargebacksRequestError';
        this.status = status;
    }
}
/** Удаление аккаунта. 409 = «есть загруженные алерты», см. `ChargebacksRequestError`. */
export async function deleteAccount(id) {
    const r = await api.del(`/api/chargebacks/accounts/${encodeURIComponent(id)}`);
    if (!r.ok)
        throw new ChargebacksRequestError(await api.extractServerError(r), r.status);
}
/**
 * Проверка соединения с источником. Ничего не пишет — только пробует ключ.
 *
 * Отказ провайдера («401 от Chargeblast») приезжает с HTTP 200 и `ok: false`:
 * это ИСХОД проверки, а не сбой запроса к нам, и мутация на нём не должна
 * уходить в `onError`.
 */
export async function testAccount(id) {
    return api.post(`/api/chargebacks/accounts/${encodeURIComponent(id)}/test`, {});
}
// ──────────────────────────────── Журналы ──────────────────────────────────
/** Сколько строк журнала показывать. Роуты ограничены сверху 200. */
export const JOURNAL_PAGE_SIZE = 100;
/** Прогоны синхронизации, новые сверху. Конверт `{items}` разворачивается здесь. */
export async function listSyncRuns(limit = JOURNAL_PAGE_SIZE) {
    const r = await api.get(`/api/chargebacks/sync-runs?limit=${limit}`);
    return r.items;
}
/**
 * Журнал ОТПРАВЛЕННЫХ РЕШЕНИЙ — история строк `risk_alert_action` по всем
 * алертам.
 *
 * TODO(admin-api): роута `GET /api/chargebacks/alert-actions` ещё НЕТ. Сегодня
 * решение видно только внутри карточки кейса (`RiskAlertDTO.sentAction`), то
 * есть по одному алерту за раз — а сверять `in_flight` («отправили, исход не
 * подтверждён») оператору нужно СПИСКОМ, иначе такую строку не находит никто.
 * Роут должен отдавать поля `RiskAlertActionLogItemDTO`, сортировку
 * `created_at DESC` и быть закрыт правом `chargebacks.read`.
 *
 * До появления роута экран честно сообщает, что журнал недоступен, и НЕ рисует
 * выдуманных строк.
 */
export async function listAlertActions(limit = JOURNAL_PAGE_SIZE) {
    return api.get(`/api/chargebacks/alert-actions?limit=${limit}`);
}
/** Собирает query-строку сводки. Вынесено отдельно, чтобы тестировать без сети. */
export function supportDashboardQueryString(period) {
    const p = new URLSearchParams();
    if (period.from)
        p.set('from', period.from);
    if (period.to)
        p.set('to', period.to);
    const qs = p.toString();
    return qs === '' ? '' : `?${qs}`;
}
/**
 * Сводные метрики поддержки за период.
 *
 * Идёт через `fetchRaw`, а не через `api.get`, ради СОХРАНЁННОГО кода ответа:
 * раздел открыт правом `chargebacks.read` (первая линия), а этот роут закрыт
 * правом `dashboard.read` (супервайзер и владелец), и 403 здесь — не сбой, а
 * штатный ответ, который экран обязан объяснить словами. Отличить его по
 * тексту сообщения нельзя: текст меняется, код — нет.
 */
export async function getSupportDashboard(period = {}) {
    const r = await api.fetchRaw(`/api/support-dashboard${supportDashboardQueryString(period)}`);
    if (!r.ok)
        throw new ChargebacksRequestError(await api.extractServerError(r), r.status);
    return (await r.json());
}
// ───────────────────────────── Правила реакций (task 13) ───────────────────
/** Список правил, уже в порядке `position`. Конверт `{rules}` разворачивается здесь. */
export async function listRules() {
    const r = await api.get('/api/chargebacks/rules');
    return r.rules;
}
export async function createRule(body) {
    return api.post('/api/chargebacks/rules', body);
}
export async function updateRule(id, body) {
    return api.patch(`/api/chargebacks/rules/${encodeURIComponent(id)}`, body);
}
/**
 * Удаление правила. 409 `rule_in_use` = «правило уже применялось» (на него
 * ссылается `risk_reaction_run.rule_id`), поэтому — `ChargebacksRequestError`
 * со статусом, как у `deleteAccount`: вызывающему нужен именно КОД ответа,
 * `extractServerError` отдаёт машинную строку `error`, не текст для человека.
 */
export async function deleteRule(id) {
    const r = await api.del(`/api/chargebacks/rules/${encodeURIComponent(id)}`);
    if (!r.ok)
        throw new ChargebacksRequestError(await api.extractServerError(r), r.status);
}
/**
 * Полная переупаковка порядка. `orderedIds` обязан содержать ВСЕ id правил —
 * сервер отвечает 422 на неполном/лишнем списке (см. `ReorderRiskReactionRulesRequest`
 * в `admin-contracts`). Возвращает свежий список: `reorderRules` — единственная
 * мутация раздела, которая меняет позицию сразу нескольких строк, и клиенту
 * дешевле получить готовый порядок в ответе, чем пересчитывать его сам.
 */
export async function reorderRules(orderedIds) {
    const r = await api.post('/api/chargebacks/rules/reorder', {
        orderedIds,
    });
    return r.rules;
}
// ────────────────────────── Прогоны реакций (task 13) ──────────────────────
/** Размер страницы журнала прогонов (keyset-пагинация). */
export const REACTION_RUNS_PAGE_SIZE = 50;
export async function listReactionRuns(filters = {}, cursor, limit = REACTION_RUNS_PAGE_SIZE) {
    const p = new URLSearchParams();
    if (filters.caseId)
        p.set('caseId', filters.caseId);
    p.set('limit', String(limit));
    if (cursor)
        p.set('cursor', cursor);
    return api.get(`/api/chargebacks/reaction-runs?${p.toString()}`);
}
// ─────────────────────── Ручной (пере)запуск реакции ────────────────────────
/**
 * Ручной (пере)запуск реакции по кейсу (`chargebacks.action`). Отвечает
 * `202 {status:'accepted'}` — сама реакция проводится воркером асинхронно.
 *
 * Идёт через `fetchRaw`, а не через `api.post`, ради СОХРАНЁННОГО кода ответа:
 * 409 («уже выполняется») и 503 («модуль реакций не сконфигурирован») —
 * разные факты, и UI обязан различать их текстом, а не гадать по подстроке.
 */
export async function reactCase(caseId) {
    const r = await api.fetchRaw(`/api/chargebacks/cases/${encodeURIComponent(caseId)}/react`, {
        method: 'POST',
    });
    if (!r.ok)
        throw new ChargebacksRequestError(await api.extractServerError(r), r.status);
    return (await r.json());
}
// ─────────────────────── Тумблеры автоматики (task 14) ──────────────────────
/** Текущее положение четырёх тумблеров автоматики реакций. */
export async function getRiskAutomation() {
    return api.get('/api/chargebacks/automation');
}
/**
 * Записывает состояние ЦЕЛИКОМ (PUT, все четыре поля обязательны — см. довод
 * у `UpdateRiskAutomationSettingsRequest` в контрактах). Отвечает новым
 * состоянием, поэтому вызывающему не нужен отдельный перечит.
 */
export async function updateRiskAutomation(body) {
    return api.put('/api/chargebacks/automation', body);
}
//# sourceMappingURL=api.js.map